import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { SearchHelperService } from 'src/app/core/utility/search-helper.service';
import { AppConfigService } from 'src/app/core/service/app-config.service';
import { CompositeFilterDescriptor, State } from '@progress/kendo-data-query';
import { Subscription } from 'rxjs';
import { PayerBillingService } from 'src/app/core/service/payer-billing.service';
import { PayerBillingListService } from 'src/app/core/service/grid-data/grid-data-payer-billing.service';
import { DataStateChangeEvent, GridDataResult, SelectAllCheckboxState, SelectionEvent } from '@progress/kendo-angular-grid';
import { SelectEvent, TabStripComponent } from '@progress/kendo-angular-layout';
import { Router } from '@angular/router';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { GenerateInvoiceRequest } from 'src/app/payer-billing/model/GenerateInvoiceRequest';
import { MessengerService } from 'src/app/core/service/messenger.service';
import { TranslateService } from '@ngx-translate/core';
import { IntlService } from '@progress/kendo-angular-intl';
import { sortBy } from 'lodash';

@Component({
  standalone: false,
  selector: 'app-payer-billing-list',
  templateUrl: './payer-billing-list.component.html',
  styleUrls: ['./payer-billing-list.component.scss']
})
export class PayerBillingListComponent implements OnInit, OnDestroy
{
  @ViewChild('tabstrip') public tabstrip: TabStripComponent;

  public enableAdvanced = false;
  public gridView: GridDataResult;
  public gridData: any = {};
  public count: number;
  public pageSizes = [5, 10, 25, 50, 100, 1000];
  public selectedInvoiceKeys: number[] = [];
  public selectAllState: SelectAllCheckboxState = 'unchecked';
  public deleteDialogOpened = false;
  public invoiceStatuses = [];
  public postRestriction: string;
  public closeRestriction: string;

  public selectedInvoicesDeleteId: any = [];
  public selectedInvoicesDeleteInvNumber: any = [];
  public selectedInvoicesPostId: any = [];
  public selectedInvoicesPostInvNumber: any = [];
  public selectedInvoicesRepostId: any = [];
  public selectedInvoicesRepostInvNumber: any = [];

  public deleteInvoicesDialogOpened = false;
  public postInvoicesDialogOpened = false;
  public closeInvoiceDialogOpened = false;
  public updateIdentifiersDialogOpened = false;

  /**
   * Payer Billing list Kendo grid state.
   */
  public state: State = {
    skip: 0,
    // Initial filter descriptor
    filter: {
      logic: 'and',
      filters: []
    },
    sort: [
      { field: 'lastModifiedOn', dir: 'desc' }
    ]
  };

  public filterText = '';
  public selectedTab = 0;
  public showRecordsTab = false;
  public selectedInvoice: any;
  public selectedInvoiceModal: any;

  private listSubscription: Subscription;
  private messagingSubscription: Subscription;

  constructor(
    private searchHelperService: SearchHelperService,
    private appConfigService: AppConfigService,
    private payerBillingService: PayerBillingService,
    private payerBillingListService: PayerBillingListService,
    private router: Router,
    private messageHandlerService: MessageHandlerService,
    private messengerService: MessengerService,
    private translate: TranslateService,
    public intl: IntlService)
  {
    this.state.take = this.appConfigService.getConfig().pageSize;
    this.messagingSubscription = this.messengerService.receiveMessage().subscribe({
      next: (message) =>
      {
        if (message.type === 'refreshInvoices')
        {
          this.getInvoices();
        }
      }
    });
  }

  /**
   * Provide loading property from Service.
   */
  public get loading(): boolean
  {
    return this.payerBillingListService ? this.payerBillingListService.loading : false;
  }

  public ngOnInit(): void
  {
    this.invoiceStatuses = sortBy(this.payerBillingService.invoiceStatuses, ['label']);
    this.listSubscription = this.payerBillingListService.subscribe({
      next: (value) =>
      {
        if (value !== null)
        {
          value.data.forEach(d =>
          {
            d.createdOn = new Date(d.createdOn);
            if (d.lastModifiedOn !== null)
            {
              d.lastModifiedOn = new Date(d.lastModifiedOn);
            }
            if (d.startDate !== null && d.startDate !== undefined && d.startDate.length === 10)
            {
              d.startDate = new Date(`${d.startDate}T12:00:00.000`);
            }
            if (d.endDate !== null && d.endDate !== undefined && d.endDate.length === 10)
            {
              d.endDate = new Date(`${d.endDate}T12:00:00.000`);
            }
            d.invoiceStatusText = this.payerBillingService.getInvoiceStatusText(d.invoiceStatus);
            d.canDeleteInvoice = this.canDeleteInvoice(d.invoiceStatus);
          });
        }

        // TODO - this interaction between the component and the data service can be simplified / clean up.
        this.gridView = {
          data: (value) ? this.gridData = value.data : this.gridData = [],
          total: (value) ? this.count = value.total : this.count = 0
        };
      }
    });
    this.getInvoices();
    this.translate.get(['POST-RESTRICTION', 'CLOSE-RESTRICTION']).subscribe({
      next: (res) =>
      {
        this.postRestriction = res['POST-RESTRICTION'];
        this.closeRestriction = res['CLOSE-RESTRICTION'];
      }
    });
  }

  public generateInvoices()
  {
    this.router.navigate(['/generate-invoice']);
  }

  public openDeleteInvoicesDialogOpened(): void
  {
    const selectedInvoicesDeleteId = [];    // Use a temporary array to perform a second validation of invoice statuses
    this.selectedInvoicesDeleteInvNumber = [];
    this.selectedInvoicesDeleteId.forEach((invoiceId) =>
    {
      this.payerBillingService.getInvoiceById(invoiceId).subscribe({
        next: (invoiceData) =>
        {
          if (this.canDeleteInvoice(invoiceData.invoiceStatus))
          {
            selectedInvoicesDeleteId.push(invoiceData.id);
            this.selectedInvoicesDeleteInvNumber.push(invoiceData.invoiceNumber);
          }
        }
      });
      this.selectedInvoicesDeleteId = selectedInvoicesDeleteId;
      this.deleteInvoicesDialogOpened = true;
    });
  }

  public openPostInvoicesDialogOpened(): void
  {
    this.selectedInvoicesPostId = [];
    this.selectedInvoicesPostInvNumber = [];
    this.selectedInvoicesRepostId = [];
    this.selectedInvoicesRepostInvNumber = [];
    let processedCount = 0;
    this.selectedInvoiceKeys.forEach((invoiceId) =>
    {
      this.payerBillingService.getInvoiceById(invoiceId).subscribe({
        next: (invoiceData) =>
        {
          processedCount++;
          if ((this.selectedInvoicesPostId.findIndex(x => x.id === invoiceData.id) === -1) && (invoiceData.invoiceStatus == "OPEN"))
          {
            this.selectedInvoicesPostId.push(invoiceData.id);
            this.selectedInvoicesPostInvNumber.push(invoiceData.invoiceNumber);
          }
          if ((this.selectedInvoicesRepostId.findIndex(x => x.id === invoiceData.id) === -1) && (invoiceData.invoiceStatus == "REJECTED"))
          {
            this.selectedInvoicesRepostId.push(invoiceData.id);
            this.selectedInvoicesRepostInvNumber.push(invoiceData.invoiceNumber);
          }
          if (processedCount === this.selectedInvoiceKeys.length)
          {
            if (this.selectedInvoicesPostId.length + this.selectedInvoicesRepostId.length > 0)
            {
              this.postInvoicesDialogOpened = true;
            }
            else
            {
              this.messageHandlerService.displayDirectMessage('error', this.postRestriction);
            }
          }
        }
      });
    });
  }

  public openCloseInvoiceDialogOpened(invoice: any): void
  {
    this.selectedInvoiceModal = invoice;
    this.payerBillingService.getInvoiceItemList(invoice.id).subscribe({
      next: (records) =>
      {
        const pendingRebillRecords = records.filter(u => u.invoiceItemStatus === 'PENDING' || u.invoiceItemStatus === 'PENDING_REBILL' || u.invoiceItemStatus === 'REBILL');
        if (pendingRebillRecords.length > 0)
        {
          this.messageHandlerService.displayDirectMessage('error', this.closeRestriction);
          return;
        }
        else
        {
          this.closeInvoiceDialogOpened = true;
        }

      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  public openCloseUpdateIdentifiersDialogOpened(invoice: any): void
  {
    this.selectedInvoiceModal = invoice;
    this.updateIdentifiersDialogOpened = true;
  }

  public deleteInvoices()
  {
    this.payerBillingService.deleteInvoices(this.selectedInvoicesDeleteId).subscribe({
      next: () =>
      {
        this.messageHandlerService.displaySuccess('INVOICES-DELETED-SUCCESSFULLY');
        this.deleteInvoicesDialogOpened = false;
        this.clearSelectedInvoices();
        this.state.skip = 0;
        this.executeQuery();
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  public postInvoices()
  {
    this.payerBillingService.updateInvoicesStatus('POSTED', this.selectedInvoicesPostId).subscribe({
      next: () =>
      {
        this.payerBillingService.updateInvoicesStatus('REPOSTED', this.selectedInvoicesRepostId).subscribe({
          next: () =>
          {
            if (this.selectedInvoicesPostId.length + this.selectedInvoicesRepostId.length === 1)
            {
              this.messageHandlerService.displaySuccess('INVOICE-POSTED-SUCCESSFULLY');
            }
            else
            {
              this.messageHandlerService.displaySuccess('INVOICES-POSTED-SUCCESSFULLY');
            }

            this.postInvoicesDialogOpened = false;
            this.clearSelectedInvoices();
            this.state.skip = 0;
            this.executeQuery();
          },
          error: (errorResponse) =>
          {
            this.messageHandlerService.displayError(errorResponse);
          }
        });
      },
      error: (errorResponse: any) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  public ngOnDestroy(): void
  {
    if (this.listSubscription !== undefined) { this.listSubscription.unsubscribe(); }
    if (this.messagingSubscription !== undefined) { this.messagingSubscription.unsubscribe(); }
  }

  public searchFieldChange(filterText: string): void
  {
    this.state.skip = 0;
    const root = this.searchHelperService.fieldChange(filterText, this.state, "invoiceNumber");
    this.filterText = filterText;
    this.filterChange(root);
  }

  /**
   * Data change event handler.
   *
   * @param state State of grid operations
   */
  public dataStateChange(state: DataStateChangeEvent): void
  {
    this.state = state;
    this.executeQuery();
  }

  /**
   * Filtering by search field
   */
  public filterChange(filter: CompositeFilterDescriptor): void
  {
    this.state.filter = filter;
    this.executeQuery();
  }

  public onSelectionChange(event: SelectionEvent): void
  {
    if (event.selectedRows[0]?.dataItem.canDeleteInvoice)
    {
      if (!this.selectedInvoicesDeleteId.includes(event.selectedRows[0].dataItem.id))
      {
        this.selectedInvoicesDeleteId = [...this.selectedInvoicesDeleteId, event.selectedRows[0].dataItem.id];
      }
    }
    else if (event?.deselectedRows)
    {
      this.selectedInvoicesDeleteId = this.selectedInvoicesDeleteId.filter((k: any) => k !== event?.deselectedRows[0]?.dataItem.id);
    }
  }

  public canDeleteInvoice(invoiceStatus: string): boolean
  {
    return invoiceStatus === 'OPEN' || invoiceStatus === 'REJECTED' || invoiceStatus === 'ERROR';
  }

  /**
   * Selected invoices.
   */
  public onSelectedKeysChange(event: any): void
  {
    this.selectedInvoiceKeys = event;
    if (this.selectedInvoiceKeys.length === 1)
    {
      this.selectedInvoice = this.gridView.data.filter(f => f.id == event[0])[0];
      if (this.selectedInvoice && this.selectedInvoice.canDeleteInvoice)
      {
        this.selectedInvoicesDeleteId = [this.selectedInvoice.id];
      }
      else
      {
        this.selectedInvoicesDeleteId = [];
      }
    }
    else
    {
      this.selectedInvoice = null;
    }
    this.showRecordsTab = this.selectedInvoice !== null;
  }

  public dblClickInvoice(event: any): void
  {
    const rowIndex = event.target.parentElement.rowIndex;
    if (typeof rowIndex === 'number' && rowIndex < this.gridView.data.length)
    {
      if (this.showRecordsTab)
      {
        this.selectedTab = 1;
        this.tabstrip.selectTab(this.selectedTab);
      }
    }
  }

  public onSelectAllChange(checkedState: SelectAllCheckboxState)
  {
    if (checkedState === 'checked')
    {
      this.selectedInvoicesDeleteId = this.gridView.data.filter((item) => (this.canDeleteInvoice(item.invoiceStatus))).map((item) => item.id);
      this.selectAllState = 'checked';
      this.selectedInvoice = null;
      this.showRecordsTab = false;
    }
    else
    {
      this.clearSelectedInvoices();
    }
  }

  /**
   * Provides the invoices selected count. This controls the 'Select' button state and count display.
   *
   * @returns Number Total count of the selected invoices.
   */
  public getSelectedCount(): number
  {
    return this.selectedInvoicesDeleteId.length;
  }

  /**
   * Sets the invoice to delete, and opens the delete invoice dialog.
   */
  public deleteHandler(id: number): void
  {
    this.payerBillingService.getInvoiceById(id).subscribe({
      next: (data) =>
      {
        this.selectedInvoice = data;
        this.deleteDialogOpened = true;
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  /**
   * Button action handler.
   *
   * @param confirm Confirm the action: yes/no
   */
  public deleteAction(confirm: boolean, invoiceId: number, displaySuccessMessage: boolean = false): void
  {
    if (confirm)
    {
      // Proceed with the deletion
      this.payerBillingService.deleteInvoice(invoiceId).subscribe({
        next: () =>
        {
          if (displaySuccessMessage)
          {
            this.messageHandlerService.displaySuccess('INVOICE-DELETED-SUCCESSFULLY');
          }
          this.selectedInvoice = null;
          this.selectedInvoiceKeys = [];
          this.selectedInvoicesDeleteId = [];
          this.getInvoices();
        },
        error: (errorResponse) =>
        {
          this.messageHandlerService.displayError(errorResponse);
        }
      });
    }
    this.deleteDialogOpened = false;
  }

  /**
   * Regenerate the invoice.
   *
   * @param id invoice id.
   */
  public regenerateHandler(invoice: any): void
  {
    // delete initial invoice
    this.payerBillingService.deleteInvoice(invoice.id).subscribe({
      next: () =>
      {
        this.selectedInvoice = null;
        this.selectedInvoiceKeys = [];
        this.selectedInvoicesDeleteId = [];
        const invoiceRequests: GenerateInvoiceRequest[] = [];
        // re-generate invoice with same parameters it was initially ran with
        const generateInvoiceRequest: GenerateInvoiceRequest =
        {
          payerId: invoice.payerId,
          feeSchedules: invoice.feeSchedules,
          startDate: invoice.startDate.toISOString().substring(0, 10),
          endDate: invoice.endDate.toISOString().substring(0, 10)
        };
        invoiceRequests.push(generateInvoiceRequest);
        this.payerBillingService.generateInvoice(invoiceRequests).subscribe({
          next: (data: any) =>
          {
            if (data !== null)
            {
              this.messageHandlerService.displaySuccess('INVOICE-REGENERATED-SUCCESSFULLY');
            }
            else        
            {
              this.messageHandlerService.displayWarning('NO-INVOICE-TO-GENERATE');
            }
            this.refresh();
          },
          error: (errorResponse) =>
          {
            this.messageHandlerService.displayError(errorResponse);
          }
        });
        this.getInvoices();
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  public closeInvoice(): void
  {
    const invoiceIds: number[] = [];
    invoiceIds.push(this.selectedInvoiceModal.id);

    this.payerBillingService.updateInvoicesStatus('CLOSED', invoiceIds).subscribe({
      next: () =>
      {
        this.messageHandlerService.displaySuccess('INVOICE-CLOSED-SUCCESSFULLY');
        this.closeInvoiceDialogOpened = false;
        this.clearSelectedInvoices();
        this.state.skip = 0;
        this.executeQuery();
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  public cancelDeleteInvoices(): void
  {
    this.deleteInvoicesDialogOpened = false;
  }

  public cancelPostInvoices(): void
  {
    this.postInvoicesDialogOpened = false;
  }

  public cancelCloseInvoice(): void
  {
    this.closeInvoiceDialogOpened = false;
  }

  public cancelCloseUpdateIdentifiers(): void
  {
    this.updateIdentifiersDialogOpened = false;
  }

  public updateInvoiceIdentifiers(): void
  {
    this.payerBillingService.updateInvoiceItemIdentifiers(this.selectedInvoiceModal.id).subscribe({
      next: () =>
      {
        this.messageHandlerService.displaySuccess('INVOICE-ITEM-IDENTIFIERS-UPDATED-SUCCESSFULLY');
        this.updateIdentifiersDialogOpened = false;
      },
      error: (errorResponse) =>
      {
        this.updateIdentifiersDialogOpened = false;
        this.messageHandlerService.displayError(errorResponse);
      }
    });

  }

  /**
   * Perform grid refresh.
   */
  public refresh(): void
  {
    this.getInvoices();
  }

  public onTabSelect(e: SelectEvent): void
  {
    const tabChanged = e.index !== this.selectedTab;
    this.selectedTab = e.index;
    // Automatically refresh
    if (tabChanged && this.selectedTab === 0)
    {
      this.getInvoices();
    }
  }

  private getInvoices(): void
  {
    this.clearSelectedInvoices();
    this.state = {
      filter: {
        filters: [], logic: 'and'
      }, group: [], skip: 0,
      sort: [
        { field: 'lastModifiedOn', dir: 'desc' }
      ],
      take: this.appConfigService.getConfig().pageSize
    };
    this.executeQuery();
  }

  private executeQuery(): void
  {
    this.showRecordsTab = false;
    this.payerBillingListService.queryByState(this.state);
  }

  private clearSelectedInvoices(): void
  {
    this.selectedInvoiceKeys = [];
    this.selectedInvoicesDeleteId = [];
    this.selectAllState = 'unchecked';
  }
}
